import { useState } from 'react';
import { categories, MAIN_SITE_URL } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }

    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      const urlParams = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string' && key !== 'website_alt') {
          urlParams.append(key, value);
        }
      });
      const res = await fetch('https://readdy.ai/api/form/d95rvk57gtqne2as9mt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlParams.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Thank you for subscribing! Stay tuned for spreadsheet updates.');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('Your submission could not be processed. Please try again.');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || 'Something went wrong. Please try again.');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-50 rounded-t-2xl mx-3 md:mx-4 mt-10 md:mt-14">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          <div className="md:col-span-1">
            <h3 className="font-heading text-xl md:text-2xl font-semibold mb-2">Stay Updated</h3>
            <p className="text-foreground-300 text-sm mb-6 leading-relaxed">
              Get the latest CSSBuy spreadsheet finds and agent shopping tips delivered to your inbox.
            </p>
            <form onSubmit={handleSubscribe} data-readdy-form="true" className="space-y-3" noValidate>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="anti-spam-field"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full bg-foreground-800 border border-foreground-700 rounded-lg px-4 py-2.5 text-sm text-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="inline-flex items-center gap-2 bg-background-50 text-foreground-950 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {formStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
                <i className="ri-arrow-right-line"></i>
              </button>
              {formStatus !== 'idle' && (
                <p className={`text-xs ${formStatus === 'success' ? 'text-accent-400' : 'text-red-400'}`}>
                  {formMessage}
                </p>
              )}
            </form>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wide mb-4">Categories</h4>
            <div className="grid grid-cols-2 gap-y-2 gap-x-4">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-300 hover:text-background-50 transition-colors underline decoration-foreground-700/50 hover:decoration-background-50 whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wide mb-4">Main Site</h4>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="text-sm text-foreground-300 hover:text-background-50 transition-colors underline decoration-foreground-700/50 hover:decoration-background-50 block mb-4"
            >
              {MAIN_SITE_URL}
            </a>
            <p className="text-xs text-foreground-400 leading-relaxed">
              Visit our main platform for the full shopping experience with thousands of curated spreadsheet finds across all major agent platforms.
            </p>
          </div>
        </div>

        <div className="border-t border-foreground-800 mt-10 md:mt-12 pt-6 md:pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline">Twitter</a>
            <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline">Instagram</a>
            <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline">Reddit</a>
          </div>
          <p className="text-sm text-foreground-400">&copy; 2026 CSSSpreadsheet.lat</p>
          <div className="flex items-center gap-6">
            <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline">Privacy</a>
            <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors underline">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}